import { type FormEvent, type ReactNode, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import * as authApi from '../api/auth';
import { TelaAuth } from '../components/TelaAuth';
import { BlocoAviso, Botao, Campo } from '../components/ui';
import { useAuth } from '../lib/auth';
import { MSG_ERRO_REDE } from '../lib/erros';
import { ApiError } from '../lib/http';
import { validarNovaSenha } from '../lib/validacaoSenha';

/**
 * Recuperação de senha da equipe (admin/atendente) — duas telas na mesma rota
 * `/recuperar`: sem `?token=` → pede e-mail/login, **resposta neutra sempre**
 * (não confirma se o identificador está cadastrado; a API envia por e-mail via
 * Resend, SPEC §8). Com `?token=` → o link recebido por e-mail: define a senha
 * nova (`POST /v1/auth/redefinir-senha`) e loga automaticamente, igual ao
 * primeiro acesso.
 */
export function RecuperarPagina(): ReactNode {
  const [params] = useSearchParams();
  const token = params.get('token');
  return token ? <FormularioNovaSenha token={token} /> : <PedirLink />;
}

function PedirLink(): ReactNode {
  const [email, setEmail] = useState('');
  const [enviado, setEnviado] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);

  const aoEnviar = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setErro(null);
    setEnviando(true);
    try {
      await authApi.recuperarSenha(email.trim());
      setEnviado(true);
    } catch (err) {
      setErro(
        err instanceof ApiError && !err.ehRede
          ? 'Não foi possível enviar agora. Tente de novo.'
          : MSG_ERRO_REDE,
      );
    } finally {
      setEnviando(false);
    }
  };

  return (
    <TelaAuth>
      {enviado ? (
        <div className="flex flex-col gap-4">
          <BlocoAviso tom="aviso">
            Se este e-mail estiver cadastrado, você vai receber um link para redefinir a senha.
          </BlocoAviso>
          <Link to="/login" className="text-nota text-acento-texto underline">
            Voltar para o login
          </Link>
        </div>
      ) : (
        <form onSubmit={(e) => void aoEnviar(e)} className="flex flex-col gap-4" noValidate>
          <p className="text-corpo text-tinta-2">Recuperar senha</p>
          {erro && <BlocoAviso tom="erro">{erro}</BlocoAviso>}
          <Campo
            rotulo="E-mail"
            type="email"
            autoComplete="email"
            autoFocus
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
          <Botao type="submit" carregando={enviando}>
            {enviando ? 'Enviando…' : 'Enviar link'}
          </Botao>
          <Link to="/login" className="text-nota text-acento-texto underline">
            Voltar para o login
          </Link>
        </form>
      )}
    </TelaAuth>
  );
}

function FormularioNovaSenha({ token }: { token: string }): ReactNode {
  const navigate = useNavigate();
  const { registrarSessao } = useAuth();

  const [senha, setSenha] = useState('');
  const [confirma, setConfirma] = useState('');
  const [erroSenha, setErroSenha] = useState<string | null>(null);
  const [erroGeral, setErroGeral] = useState<string | null>(null);
  const [linkInvalido, setLinkInvalido] = useState(false);
  const [enviando, setEnviando] = useState(false);

  if (linkInvalido) {
    return (
      <TelaAuth>
        <div className="flex flex-col gap-4">
          <BlocoAviso tom="erro">Este link expirou ou já foi usado.</BlocoAviso>
          <Link to="/recuperar" className="text-nota text-acento-texto underline">
            Pedir um novo link
          </Link>
        </div>
      </TelaAuth>
    );
  }

  const aoEnviar = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setErroSenha(null);
    setErroGeral(null);

    const erro = validarNovaSenha(senha, confirma);
    if (erro) {
      setErroSenha(erro);
      return;
    }

    setEnviando(true);
    try {
      const dados = await authApi.redefinirSenha(token, senha);
      registrarSessao({
        accessToken: dados.accessToken,
        refreshToken: dados.refreshToken,
        usuario: authApi.usuarioDeResposta(dados),
      });
      navigate('/', { replace: true });
    } catch (err) {
      if (err instanceof ApiError && !err.ehRede && err.codigo === 'nao_autenticado') {
        setLinkInvalido(true);
      } else {
        setErroGeral('Não foi possível redefinir sua senha agora. Tente de novo.');
      }
      setEnviando(false);
    }
  };

  return (
    <TelaAuth>
      <form onSubmit={(e) => void aoEnviar(e)} className="flex flex-col gap-4" noValidate>
        <p className="text-corpo text-tinta-2">Criar senha nova</p>
        {erroGeral && <BlocoAviso tom="erro">{erroGeral}</BlocoAviso>}
        <Campo
          rotulo="Nova senha"
          type="password"
          autoComplete="new-password"
          autoFocus
          ajuda="Mínimo de 8 caracteres."
          erro={erroSenha ?? undefined}
          value={senha}
          onChange={(e) => setSenha(e.target.value)}
        />
        <Campo
          rotulo="Confirmar senha"
          type="password"
          autoComplete="new-password"
          value={confirma}
          onChange={(e) => setConfirma(e.target.value)}
        />
        <Botao type="submit" carregando={enviando}>
          {enviando ? 'Salvando…' : 'Salvar nova senha'}
        </Botao>
      </form>
    </TelaAuth>
  );
}
